import { useState, useEffect, Dispatch, SetStateAction } from 'react';
import { SetValueConfig } from 'react-hook-form';
import { MediumDataKey } from 'modules/templateEditor/common/types';
import { CopyAssistDropdown } from 'modules/ai/copyAssist/components/dropdown/CopyAssistDropdown';
import { CopyAssistDropdownFooter } from 'modules/ai/copyAssist/components/dropdown/CopyAssistDropdownFooter';
import { CopyAssistDropdownBodyContent } from 'modules/ai/copyAssist/components/dropdown/CopyAssistDropdownBodyContent';
import { InputHighlightOverlay } from 'modules/ai/copyAssist/components/InputHighlightOverlay';
import { RUMHelpers } from 'services/observability/datadog';
import { generateActionName } from 'modules/ai/copyAssist/observabilityUtils';
import { CopyAssistSuggestionsAsVariants } from 'modules/experiments/components/ui';

interface CommonCopyAssisDropdownWithHighlightProps {
  value: string;
  /* ref for the element to target */
  targetRef?: React.RefObject<HTMLDivElement> | null;
  setSelectedOption: Dispatch<SetStateAction<string>>;
  selectedOption: string;
  loading: boolean;
  onResetBtnClick: () => void;
  isError?: boolean;
  generatedOptions: string[];
  addSuggestionsAsVariants: boolean;
}

export interface CopyAssisDropdownWithHighlightPropsWithMediumDataKey
  extends CommonCopyAssisDropdownWithHighlightProps {
  mediumDataKey: MediumDataKey;
  /* function called to set the field for the template */
  setCurrentMediumField: (fieldName: MediumDataKey, value: any) => void;
}

interface CopyAssisDropdownWithHighlightPropsWithFieldName
  extends CommonCopyAssisDropdownWithHighlightProps {
  fieldName: string;
  /* the setValue method from the RHF's useFormContext  */
  setField: (fieldName: string, value: any, options?: SetValueConfig) => void;
}

export type CopyAssisDropdownWithHighlightProps =
  | CopyAssisDropdownWithHighlightPropsWithMediumDataKey
  | CopyAssisDropdownWithHighlightPropsWithFieldName;

export const CopyAssistDropdownWithSelectionHighlight = (
  props: CopyAssisDropdownWithHighlightProps,
) => {
  const {
    value,
    targetRef,
    setSelectedOption,
    selectedOption,
    loading,
    onResetBtnClick,
    generatedOptions,
    isError,
    addSuggestionsAsVariants,
  } = props;
  const hasMediumDataKey = 'mediumDataKey' in props;
  const fieldKey = hasMediumDataKey ? props.mediumDataKey : props.fieldName;
  const [highlight, setHighlight] = useState(false);

  const onCopySuggestionClick = (index: number) => {
    RUMHelpers.addRUMAction(generateActionName('Copy Suggestion Selected'));
    const newSelection = index.toString();
    setSelectedOption(newSelection);

    if (fieldKey) {
      const fieldValue = generatedOptions[index];
      if (hasMediumDataKey) {
        props.setCurrentMediumField(props.mediumDataKey, fieldValue);
      } else {
        props.setField(props.fieldName, fieldValue, {
          shouldValidate: true,
          shouldDirty: true,
          shouldTouch: true,
        });
      }
      if (targetRef && targetRef?.current) {
        targetRef?.current.focus();
      }
    }

    //  handle highlight animation
    setHighlight(true);
    setTimeout(() => {
      setHighlight(false);
    }, 1000);
  };

  useEffect(() => {
    if (generatedOptions.length > 0 && generatedOptions[parseInt(selectedOption, 10)] !== value) {
      setSelectedOption('');
    }
  }, [value, selectedOption]);

  return (
    <div>
      <CopyAssistDropdown targetRef={targetRef}>
        <CopyAssistDropdownBodyContent
          generatedOptions={generatedOptions}
          isLoading={loading}
          onCopySuggestionClick={onCopySuggestionClick}
          selected={selectedOption}
          isError={isError}
        />
        {addSuggestionsAsVariants && (
          <CopyAssistSuggestionsAsVariants
            isLoading={loading}
            isError={isError}
            suggestions={generatedOptions}
          />
        )}
        <CopyAssistDropdownFooter onResetButtonClick={onResetBtnClick} isLoading={loading} />
      </CopyAssistDropdown>
      {highlight ? (
        <InputHighlightOverlay
          value={generatedOptions[parseInt(selectedOption, 10)]}
          targetRef={targetRef}
        />
      ) : null}
    </div>
  );
};
